import { Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import { Trans, useTranslation } from "react-i18next";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../../api/__generated__";
import Button from "../../../../../components/ui/buttons/Button";
import environment from "../../../../../environment/environment";
import {
  BloomUpNamespacesEnum,
  I18Namespaces,
} from "../../../../language/I18Namespaces";
import { I18NexusLanguages } from "../../../../language/languagesUtil";
import { CodeModal } from "./CodeModal";
import { ReactComponent as Directions } from "./Directions.svg";
import { ReactComponent as Offline } from "./Offline.svg";

const organizationApproachFragment = graphql(`
  fragment OrganizationApproach on Organization {
    id
    coachingEnabled
    coachingsPerHuman
    code
    consultsPerHuman
    creditsPerHuman
    mindlabBusinessId
    mindlabEnabled
    pobosEnabled
    ssoEnabled
    videoEnabled
  }
`);

const learnLanguagesMap = new Map<I18NexusLanguages, string>([
  [I18NexusLanguages.FLEMISH, "nl"],
  [I18NexusLanguages.FRENCH, "fr"],
  [I18NexusLanguages.ENGLISH, "en"],
]);

const CardContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flex: "1 1 300px",
  flexDirection: "column",
  justifyContent: "space-between",
  maxWidth: "21rem",
  minWidth: "280px",
  textAlign: "center",
  [theme.breakpoints.down(768)]: {
    flex: "1 1 100%",
    maxWidth: "100%",
    minWidth: "auto",
    padding: "0 1rem",
  },
}));

const CardContent = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  [theme.breakpoints.down(640)]: {
    gap: "1rem",
  },
}));

const TwoColumnContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: "2rem",
  justifyContent: "space-around",
  margin: "0 6rem",
  [theme.breakpoints.down(1100)]: {
    margin: "0 2rem",
  },
  [theme.breakpoints.down(768)]: {
    flexDirection: "column",
    gap: "1.5rem",
    margin: "0",
    textAlign: "center",
  },
  [theme.breakpoints.down(640)]: {
    gap: "1rem",
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  height: "auto",
  maxWidth: "420px",
  width: "100%",
  [theme.breakpoints.down(768)]: {
    maxWidth: "80%",
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  maxWidth: "480px",
  [theme.breakpoints.down(640)]: {
    gap: "0.75rem",
  },
}));

const TextContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  marginBottom: "1rem",
});

const Approach = ({
  backgroundColor,
  organization,
}: {
  backgroundColor: string;
  organization: FragmentType<typeof organizationApproachFragment>;
}) => {
  const { i18n, t } = useTranslation<I18Namespaces>([
    BloomUpNamespacesEnum.Organization,
  ]);

  const learnLanguage = learnLanguagesMap.get(
    i18n.language as I18NexusLanguages,
  );

  const organizationData = getFragmentData(
    organizationApproachFragment,
    organization,
  );

  const {
    coachingEnabled,
    code,
    mindlabBusinessId,
    mindlabEnabled,
    pobosEnabled,
    ssoEnabled,
    videoEnabled,
  } = organizationData;

  return (
    <Box
      sx={(theme) => ({
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        gap: "6rem",
        justifyContent: "center",
        padding: "0 96px",
        [theme.breakpoints.down(1024)]: {
          gap: "4rem",
          padding: "0 6rem",
        },
        [theme.breakpoints.down(768)]: {
          gap: "3rem",
          padding: "0 3rem",
        },
        [theme.breakpoints.down(640)]: {
          gap: "2.5rem",
          padding: "0 2rem",
        },
        [theme.breakpoints.down(400)]: {
          gap: "2rem",
          padding: "0 1.5rem",
        },
        [theme.breakpoints.down(320)]: {
          gap: "1.5rem",
          padding: "0 1rem",
        },
      })}
    >
      {pobosEnabled ? (
        <Box
          sx={(theme) => ({
            display: "flex",
            gap: "3rem",
            justifyContent: "center",
            [theme.breakpoints.down(768)]: {
              alignItems: "center",
              flexDirection: "column",
              gap: "2rem",
            },
            [theme.breakpoints.down(640)]: {
              gap: "1.5rem",
            },
          })}
        >
          <CardContainer>
            <CardContent>
              <Box
                component={Directions}
                sx={{
                  height: "auto",
                  maxHeight: "11rem",
                  maxWidth: "15rem",
                  width: "100%",
                }}
              />
              <Box>
                <Typography variant="h2">
                  {t("organization:landing.approach.bloomup.title")}
                </Typography>
                <Typography sx={{ marginTop: "0.75rem" }}>
                  {t("organization:landing.approach.bloomup.body")}
                </Typography>
              </Box>
            </CardContent>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <img
                alt="logo BloomUp"
                src="https://bloomup-production-www.s3.eu-central-1.amazonaws.com/Logo_d2b040aaa2.svg"
                style={{ marginTop: "0.75rem", maxHeight: "2.25rem" }}
              />
              <Button
                href={
                  ssoEnabled
                    ? `/login/sso?redirectUrl=${encodeURI(
                        environment.REACT_APP_URL + "/match",
                      )}`
                    : `/match?code=${code}`
                }
                label={t("organization:landing.approach.buttons.startMatching")}
                sx={{ marginTop: "0.5rem" }}
                variant="contained"
              />
            </Box>
          </CardContainer>
          <CardContainer>
            <CardContent>
              <Box
                component={Offline}
                sx={{
                  height: "auto",
                  maxHeight: "11rem",
                  maxWidth: "15rem",
                  width: "100%",
                }}
              />
              <Box>
                <Typography variant="h2">
                  {t("organization:landing.approach.pobos.title")}
                </Typography>
                <Typography sx={{ marginTop: "0.75rem" }}>
                  {t("organization:landing.approach.pobos.body")}
                </Typography>
              </Box>
            </CardContent>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <img
                alt="logo Pobos"
                src="https://public-assets.bloomup.org/organization-logos/Pobos.svg"
                style={{ marginTop: "0.75rem", maxHeight: "2.25rem" }}
              />
              <Button
                href="tel:+ 32 800  11 0 11"
                label={t("organization:landing.approach.pobos.phone")}
                sx={{ marginTop: "0.5rem" }}
              />
            </Box>
          </CardContainer>
        </Box>
      ) : mindlabEnabled && !coachingEnabled && !videoEnabled ? ( //Show Learn big instead of matching
        <TwoColumnContainer>
          <ImageContainer>
            <img
              alt="selfhelp"
              src="https://production-guidedgrowthtracks.s3.eu-central-1.amazonaws.com/shared/plants/ID%3D76-selfhelp%2C+Size%3DSmall.png"
              style={{ height: "auto", maxWidth: "100%" }}
            />
          </ImageContainer>
          <ContentContainer>
            <TextContent>
              <Typography variant="h2">
                {t("match:treatmentStep.treatments.selfHelp.title")}
              </Typography>
              <Typography>
                {t("match:treatmentStep.treatments.selfHelp.desc")}
              </Typography>
            </TextContent>
            {ssoEnabled ? (
              <Button
                fullyColored
                label={t("organization:landing.approach.selfHelp.button")}
                onClick={() => {
                  window.open(
                    `${environment.MINDLAB_FRONTEND_URL}/${learnLanguage}/${
                      "login/sso/" + mindlabBusinessId
                    }`,
                  );
                }}
                variant="contained"
              />
            ) : (
              code && (
                <CodeModal
                  code={code}
                  continueText={t(
                    "organization:landing.approach.selfHelp.modal.continue",
                  )}
                  description={t(
                    "organization:landing.approach.selfHelp.modal.description",
                  )}
                  onContinue={() => {
                    window.open(
                      `${environment.MINDLAB_FRONTEND_URL}/${learnLanguage}/${"login"}`,
                    );
                  }}
                  title={t(
                    "organization:landing.approach.selfHelp.modal.title",
                  )}
                >
                  <Button
                    fullyColored
                    label={t("organization:landing.approach.selfHelp.button")}
                    sx={{ marginTop: "0.5rem" }}
                  />
                </CodeModal>
              )
            )}
          </ContentContainer>
        </TwoColumnContainer>
      ) : (
        <TwoColumnContainer>
          <ImageContainer>
            <Box
              component={Directions}
              sx={{ height: "auto", width: "100%" }}
            />
          </ImageContainer>
          <ContentContainer>
            <TextContent>
              <Typography variant="h2">
                {t("organization:landing.approach.single.title")}
              </Typography>
              <Typography>
                {t("organization:landing.approach.bloomup.body")}
              </Typography>
            </TextContent>
            <Button
              fullyColored
              href={
                ssoEnabled
                  ? `/login/sso?redirectUrl=${encodeURI(
                      environment.REACT_APP_URL + "/match",
                    )}`
                  : `/match?code=${code}`
              }
              label={t("organization:landing.approach.buttons.startMatching")}
              variant="contained"
            />
          </ContentContainer>
        </TwoColumnContainer>
      )}
      {coachingEnabled || videoEnabled ? (
        <Box
          sx={(theme) => ({
            alignItems: "stretch",
            display: "flex",
            flexWrap: "wrap",
            gap: "2rem",
            justifyContent: "center",
            [theme.breakpoints.up(700)]: {
              gap: "3rem",
            },
            [theme.breakpoints.down(640)]: {
              gap: "1.5rem",
            },
          })}
        >
          {mindlabEnabled ? (
            <CardContainer>
              <CardContent>
                <img
                  alt="selfhelp"
                  src="https://production-guidedgrowthtracks.s3.eu-central-1.amazonaws.com/shared/plants/ID%3D76-selfhelp%2C+Size%3DSmall.png"
                  style={{ maxWidth: "15rem" }}
                />
                <Box>
                  <Typography variant="h2">
                    {t("match:treatmentStep.treatments.selfHelp.title")}
                  </Typography>
                  <Typography sx={{ marginTop: "0.75rem" }}>
                    {t("match:treatmentStep.treatments.selfHelp.desc")}
                  </Typography>
                </Box>
              </CardContent>
              {ssoEnabled ? (
                <Button
                  label={t("organization:landing.approach.selfHelp.button")}
                  onClick={() => {
                    window.open(
                      `${environment.MINDLAB_FRONTEND_URL}/${learnLanguage}/${
                        "login/sso/" + mindlabBusinessId
                      }`,
                    );
                  }}
                  sx={{ marginTop: "0.5rem" }}
                />
              ) : (
                code && (
                  <CodeModal
                    code={code}
                    continueText={t(
                      "organization:landing.approach.selfHelp.modal.continue",
                    )}
                    description={t(
                      "organization:landing.approach.selfHelp.modal.description",
                    )}
                    onContinue={() => {
                      window.open(
                        `${environment.MINDLAB_FRONTEND_URL}/${learnLanguage}/${"login"}`,
                      );
                    }}
                    title={t(
                      "organization:landing.approach.selfHelp.modal.title",
                    )}
                  >
                    <Button
                      label={t("organization:landing.approach.selfHelp.button")}
                      sx={{ marginTop: "0.5rem" }}
                    />
                  </CodeModal>
                )
              )}
            </CardContainer>
          ) : null}
          {coachingEnabled ? (
            <CardContainer>
              <CardContent>
                <img
                  alt="coaching"
                  src="https://production-guidedgrowthtracks.s3.eu-central-1.amazonaws.com/shared/plants/ID%3D75-coaching%2C+Size%3DSmall.png"
                  style={{ maxWidth: "15rem" }}
                />
                <Box>
                  <Typography variant="h2">
                    {t("match:treatmentStep.treatments.coaching.title")}
                  </Typography>
                  <Typography
                    sx={{
                      "& strong": {
                        color: "#4F7D5F",
                      },
                      marginTop: "0.75rem",
                    }}
                  >
                    <Trans
                      components={{ bold: <strong /> }}
                      i18nKey="match:treatmentStep.treatments.coaching.desc"
                    />
                  </Typography>
                </Box>
              </CardContent>
              <Button
                href={
                  ssoEnabled
                    ? `/login/sso?redirectUrl=${encodeURI(
                        environment.REACT_APP_URL + "/home/consultations",
                      )}`
                    : `/login`
                }
                label={t("organization:landing.approach.coaching.button")}
                sx={{ marginTop: "0.5rem" }}
              />
            </CardContainer>
          ) : null}
          {videoEnabled ? (
            <CardContainer>
              <CardContent>
                <img
                  alt="coaching"
                  src="https://production-guidedgrowthtracks.s3.eu-central-1.amazonaws.com/shared/plants/ID%3D77-therapy%2C+Size%3DSmall.png"
                  style={{ maxWidth: "15rem" }}
                />
                <Box>
                  <Typography variant="h2">
                    {t("match:treatmentStep.treatments.therapy.title")}
                  </Typography>
                  <Typography
                    sx={{
                      "& strong": {
                        color: "#1C7CBA",
                      },
                      marginTop: "0.75rem",
                    }}
                  >
                    <Trans
                      components={{ bold: <strong /> }}
                      i18nKey="match:treatmentStep.treatments.therapy.desc"
                    />
                  </Typography>
                </Box>
              </CardContent>
              <Button
                href={
                  ssoEnabled
                    ? `/login/sso?redirectUrl=${encodeURI(
                        environment.REACT_APP_URL + "/home/consultations",
                      )}`
                    : `/login`
                }
                label={t("organization:landing.approach.therapy.button")}
                sx={{ marginTop: "0.5rem" }}
              />
            </CardContainer>
          ) : null}
        </Box>
      ) : null}
      {mindlabEnabled && !ssoEnabled && code ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            textAlign: "center",
          }}
        >
          <Typography variant="h3">
            {t("organization:landing.approach.selfhelp.code.title")}
          </Typography>
          <Typography>
            {t("organization:landing.approach.selfhelp.code.description")}
          </Typography>
          <Box
            sx={{
              backgroundColor,
              borderRadius: "60% 40% 50% 70% / 45% 65% 55% 75%",
              display: "inline-block",
              padding: "1.5rem 2rem",
              position: "relative",
              transform: "rotate(-1.5deg)",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                transform: "rotate(1.5deg)",
              }}
              variant="h2"
            >
              {code}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default Approach;
